import { Box, Text } from '@mantine/core';
import { Link } from 'react-router-dom';
import { forwardRef, useState, type CSSProperties, type ReactElement, type ReactNode, type Ref } from 'react';
import { type VizAccent } from '@/design/VizAccents';

/** A column of the heatmap: header text, an optional second header line (e.g. a total), and an optional drill link. */
export interface HeatMapColumn {
    key: string;
    label: string;
    sublabel?: string;
    href?: string;
}

/** A row of the heatmap: header text, an optional drill link (shared by the row header and its Total cell), and the row's total. */
export interface HeatMapRow {
    key: string;
    label: string;
    href?: string;
    total: number;
}

/** The value at a (row × column) intersection and, when set, where clicking it drills. */
export interface HeatMapCell {
    value: number;
    /** Drives colour intensity when the colour should track a different measure than the displayed `value` (e.g. cost-weighted colour, score shown). Defaults to `value`. */
    colorValue?: number;
    /** Small dimmed cue rendered under the value (e.g. a `$$$` cost tier). */
    sublabel?: string;
    href?: string;
}

export interface HeatMapProps {
    rows: HeatMapRow[];
    columns: HeatMapColumn[];
    /** Value + drill target for a (row, column) pair; return a zero value for blanks. */
    cellAt: (rowKey: string, columnKey: string) => HeatMapCell;
    /** Largest value across the grid — drives colour intensity. */
    maxValue: number;
    /** Largest `colorValue` across the grid — drives intensity when cells set `colorValue`. Defaults to `maxValue`. */
    maxColorValue?: number;
    /** @deprecated Cells use the brand colour now; accent is ignored. */
    accent?: VizAccent;
    /** Top-left label naming the row axis. */
    cornerLabel: string;
    /** Noun for what each cell counts, e.g. "prompts" — used in tooltips and the caption. */
    metricNoun: string;
    /** What deeper colour means in the caption ("more" by default; e.g. "higher cost" when colour tracks a separate measure). */
    intensityNoun?: string;
    /** Formats a cell/total value for display — callers own domain formatting so this primitive stays domain-free. */
    formatValue: (value: number) => string;
    /** Min width (px) per value column; time buckets pack tighter than cross-tab columns. */
    columnMinWidth?: number;
}

const STICKY_HEADER: CSSProperties = { position: 'sticky', top: 0, zIndex: 2, background: 'var(--bm-color-surface)' };
const CELL_BASE: CSSProperties = { display: 'flex', alignItems: 'center', minHeight: 44, borderTop: '1px solid var(--bm-color-border-subtle)' };

/**
 * A grid cell that drills to `href` when set (with a hover affordance) or renders inert otherwise.
 * Forwards its ref so a `Tooltip` can wrap it. A wrapping `Tooltip` clones the child with its own
 * `className`, so we pull `className` out of `others` (or it would clobber `hoverClass`) and merge the two.
 */
const DrillCell = forwardRef<HTMLElement, { href?: string; hoverClass: string; style: CSSProperties; children?: ReactNode; className?: string; onMouseEnter?: () => void; onMouseLeave?: () => void }>(function DrillCell(
    { href, hoverClass, style, children, className, ...others },
    ref,
): ReactElement {
    const merged = className ? `${hoverClass} ${className}` : hoverClass;
    if (href !== undefined) {
        return (
            <Box ref={ref as Ref<HTMLAnchorElement>} component={Link} to={href} className={merged} style={{ ...style, textDecoration: 'none', color: 'inherit', cursor: 'pointer' }} {...others}>
                {children}
            </Box>
        );
    }
    return (
        <Box ref={ref as Ref<HTMLDivElement>} style={style} {...others}>
            {children}
        </Box>
    );
});

/**
 * A scrollable cross-tab heatmap: a labelled row axis, a column axis, a trailing
 * Total column, and colour-intensity cells. Purely presentational — callers pass
 * a normalised model plus a `cellAt` lookup, so {@link MatrixViz} (field × field)
 * and {@link TimeMatrixViz} (field × time) are thin adapters over it. Headers stay
 * pinned while the body scrolls in both directions.
 */
export default function HeatMap({ rows, columns, cellAt, maxValue, maxColorValue, cornerLabel, metricNoun, intensityNoun, formatValue, columnMinWidth = 64 }: HeatMapProps): ReactElement {
    const colorDenom = maxColorValue ?? maxValue;
    const template = `minmax(150px, 1.4fr) repeat(${columns.length}, minmax(${columnMinWidth}px, 1fr)) 64px`;
    const [hovered, setHovered] = useState<{ rowKey: string; colKey: string } | null>(null);
    const axisHighlight: CSSProperties = { background: 'var(--bm-color-zinc-hover)' };

    return (
        <>
            <Box style={{ border: '1px solid var(--bm-color-border)', overflow: 'auto', maxHeight: 'calc(100vh - 320px)', position: 'relative' }}>
                <Box style={{ display: 'grid', gridTemplateColumns: template, minWidth: 'min-content' }}>
                    <Box style={{ padding: '10px 14px', borderBottom: '1px solid var(--bm-color-border)', borderRight: '1px solid var(--bm-color-border-subtle)', ...STICKY_HEADER }}>
                        <Text size="xs" c="dimmed" fw={700} tt="uppercase" style={{ fontSize: '9px', letterSpacing: '0.08em' }}>
                            {cornerLabel}
                        </Text>
                    </Box>
                    {columns.map((col) => {
                        const inner = (
                            <>
                                <Text size="xs" fw={700} truncate>
                                    {col.label}
                                </Text>
                                {col.sublabel !== undefined && (
                                    <Text c="dimmed" ff="monospace" style={{ fontSize: '9px' }}>
                                        {col.sublabel}
                                    </Text>
                                )}
                            </>
                        );
                        const headerStyle: CSSProperties = { padding: '10px 8px', borderBottom: '1px solid var(--bm-color-border)', textAlign: 'center', ...STICKY_HEADER, ...(col.key === hovered?.colKey ? axisHighlight : {}) };
                        return col.href !== undefined ?
                                <Box key={col.key} component={Link} to={col.href} className="topic-drill-row" style={{ ...headerStyle, textDecoration: 'none', color: 'inherit' }}>
                                    {inner}
                                </Box>
                            :   <Box key={col.key} style={headerStyle}>
                                    {inner}
                                </Box>;
                    })}
                    <Box style={{ padding: '10px 8px', borderBottom: '1px solid var(--bm-color-border)', textAlign: 'center', ...STICKY_HEADER }}>
                        <Text size="xs" c="dimmed" fw={700} tt="uppercase" style={{ fontSize: '9px' }}>
                            Total
                        </Text>
                    </Box>

                    {rows.map((row) => (
                        <Box key={row.key} style={{ display: 'contents' }}>
                            <DrillCell href={row.href} hoverClass="topic-drill-row" style={{ ...CELL_BASE, padding: '0 14px', borderRight: '1px solid var(--bm-color-border-subtle)', ...(row.key === hovered?.rowKey ? axisHighlight : {}) }}>
                                <Text size="xs" fw={600} truncate>
                                    {row.label}
                                </Text>
                            </DrillCell>
                            {columns.map((col) => {
                                const cell = cellAt(row.key, col.key);
                                const heat = cell.colorValue ?? cell.value;
                                const intensity = colorDenom === 0 ? 0 : heat / colorDenom;
                                return (
                                    <DrillCell
                                        key={col.key}
                                        href={cell.href}
                                        hoverClass="insight-matrix-cell"
                                        onMouseEnter={() => setHovered({ rowKey: row.key, colKey: col.key })}
                                        onMouseLeave={() => setHovered(null)}
                                        style={{ ...CELL_BASE, justifyContent: 'center', borderLeft: '1px solid var(--bm-color-border-subtle)', background: heat === 0 ? undefined : `color-mix(in srgb, var(--bm-color-brand-accent) ${Math.round((0.14 + intensity * 0.86) * 100)}%, transparent)` }}
                                    >
                                        {cell.value !== 0 && (
                                            <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.1 }}>
                                                <Text size="sm" fw={700} ff="monospace" style={{ color: intensity > 0.5 ? 'var(--bm-color-on-accent)' : 'var(--bm-color-text)' }}>
                                                    {formatValue(cell.value)}
                                                </Text>
                                                {cell.sublabel !== undefined && cell.sublabel.length > 0 && (
                                                    <Text ff="monospace" style={{ fontSize: 9, letterSpacing: '0.04em', color: intensity > 0.5 ? 'var(--bm-color-on-accent)' : 'var(--bm-color-text-dimmed)', opacity: 0.85 }}>
                                                        {cell.sublabel}
                                                    </Text>
                                                )}
                                            </Box>
                                        )}
                                    </DrillCell>
                                );
                            })}
                            <DrillCell href={row.href} hoverClass="topic-drill-row" style={{ ...CELL_BASE, justifyContent: 'center', borderLeft: '1px solid var(--bm-color-border-subtle)', background: 'var(--bm-color-surface)' }}>
                                <Text size="sm" fw={700} ff="monospace">
                                    {formatValue(row.total)}
                                </Text>
                            </DrillCell>
                        </Box>
                    ))}
                </Box>
            </Box>
            <Text size="xs" c="dimmed" mt={8}>
                Each cell shows {metricNoun} · darker = {intensityNoun ?? 'more'}
            </Text>
        </>
    );
}
